import type { ReservationValidation } from '@/data/models/reservation.model';

import { isLowConfidenceError } from '../machines/tripLegConfidence';

const ADDRESS_FIELD_NAMES = new Set(['pickupaddress', 'dropoffaddress']);

/**
 * The /validate backend returns `pickUpAddress` / `dropOffAddress` errors (e.g.
 * `DISTANCE_CALCULATION_FAILED_SERVICE_ERROR`) for a trip leg when only one side
 * of the leg was submitted — distance-between-pickup-and-dropoff cannot be
 * computed without both addresses. Those errors are an artifact of the
 * incomplete payload, not a problem with the address the user did enter, so
 * suppress them when the matching leg in the request omitted either address.
 */
export function filterAddressErrorsForOneSidedTripLegs(
  errors: ReservationValidation[],
  payload: unknown,
): ReservationValidation[] {
  return errors.filter((e) => !shouldDropAddressError(e, payload));
}

function shouldDropAddressError(
  error: ReservationValidation,
  payload: unknown,
): boolean {
  // Don't filter out low confidence errors since they can apply to single address
  if (isLowConfidenceError(error)) return false;

  if (!ADDRESS_FIELD_NAMES.has((error.fieldName ?? '').trim().toLowerCase())) {
    return false;
  }
  if (error.tripLegSequence == null) return false;
  if (typeof payload !== 'object' || payload === null) return false;

  const tripLegs = (payload as { tripLegs?: unknown[] }).tripLegs;
  if (!Array.isArray(tripLegs)) return false;

  const leg = tripLegs[error.tripLegSequence - 1];
  if (typeof leg !== 'object' || leg === null) return false;

  const legObj = leg as Record<string, unknown>;
  return (
    !hasAddress(legObj.pickUpAddress) || !hasAddress(legObj.dropOffAddress)
  );
}

function hasAddress(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.keys(value as object).length > 0
  );
}
